import { Plus, Trash2 } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { errorMessage } from '@/lib/errors';
import { fromMinor } from '@/lib/money';
import { cn } from '@/lib/utils';
import { ADVISORY_BLOCKERS, liveLines, staleFieldError, suggestionIsCurrent, type ConflictRow, type DraftEvent, type DraftLine, type DraftState, type LineKind } from '@/lib/settleDraft';
import type { SettlePreviewAnswer } from '@/lib/finance';
import type { FinanceCategory } from '@/api/generated/types.gen';

// The settle draft's screens (OH-193 spec §4.4 p.7, §6), shared by the
// single-target windows (ConfirmReceiptDialog, SettleDialog) and the batch.
// They only render the draft and dispatch its events: every decision —
// what is a conflict, when Reset is possible — is lib/settleDraft's.

type Dispatch = (e: DraftEvent) => void;

const rowLabel = (r: ConflictRow, labelFor?: (key: ConflictRow['key']) => string) => {
  if (labelFor) return labelFor(r.key);
  return r.key.startsWith('rate:') ? `${r.key.slice('rate:'.length)} rate` : 'Remaining';
};

/** «Was → now» per target and rate, with the two ways out — visible at the moment of the decision ([R3-5]). */
export function DraftConflictPanel({
  state,
  dispatch,
  labelFor,
}: {
  state: DraftState;
  dispatch: Dispatch;
  labelFor?: (key: ConflictRow['key']) => string;
}) {
  if (state.status !== 'conflict') return null;
  const ready = suggestionIsCurrent(state);
  return (
    <div role="alert" className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--ollio-gold-deep)] p-3 text-sm">
      <p className="font-semibold text-[var(--text-heading)]">The plan or a rate changed since these amounts were filled in.</p>
      <table className="w-full text-left" aria-label="What changed">
        <thead>
          <tr className="text-xs text-muted">
            <th className="font-normal" />
            <th className="font-normal">Was</th>
            <th className="font-normal">Now</th>
          </tr>
        </thead>
        <tbody>
          {state.conflict.map((r) => (
            <tr key={r.key}>
              <td>{rowLabel(r, labelFor)}</td>
              <td className="tabular-nums">{r.was}</td>
              <td className="tabular-nums">{r.now}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={!ready} onClick={() => dispatch({ type: 'keepMyAmounts' })}>
          Keep my amounts
        </Button>
        <Button type="button" variant="secondary" size="sm" disabled={!ready} onClick={() => dispatch({ type: 'applySuggestion' })}>
          Apply the new suggestion
        </Button>
      </div>
    </div>
  );
}

/** The amounts were typed for another account/currency/target set; only re-entry or a reset to the CURRENT suggestion helps ([PR-01]). */
export function DraftStalePanel({ state, dispatch }: { state: DraftState; dispatch: Dispatch }) {
  if (state.status !== 'stale') return null;
  const staleCurrency =
    [state.bank, ...Object.values(state.targetAmounts), ...state.lines.map((l) => l.amount)].find((m) => m?.stale)?.currency ??
    state.context.currency;
  const ready = suggestionIsCurrent(state);
  return (
    <div role="status" className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--ollio-gold-deep)] p-3 text-sm">
      <p>
        {staleCurrency !== state.context.currency
          ? `These amounts were entered for ${staleCurrency}. Reset to the suggestion or re-enter them.`
          : 'These amounts were entered before the account or the payments changed. Reset to the suggestion or re-enter them.'}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={!ready} onClick={() => dispatch({ type: 'applySuggestion' })}>
          Reset to suggestion
        </Button>
        {!ready && <span className="text-xs text-muted">Waiting for the suggestion for the new account…</span>}
      </div>
    </div>
  );
}

const WARNING_TEXT: Record<string, string> = {
  no_rate_for_fee_fixed_part: 'No exchange rate for the fee’s fixed part on this date — the fee is suggested without it.',
};

const PURPOSE_TEXT: Record<string, string> = { movement: 'movement', fee_fixed: 'fee fixed part' };

/**
 * The shortfall the server suggested: with its line's amount when it suggested
 * one («Shortfall 24.20 USD — 1.39% of plan»), else the percent alone. It prints
 * the server's suggestion, not the amount typed on the draft's line;
 * DraftSuggestionInfo shows it only while the draft holds a live shortfall line.
 */
function shortfallText(resp: SettlePreviewAnswer['response'] | undefined, currency: string) {
  if (resp?.shortfall_percent_bp == null) return null;
  const pct = (resp.shortfall_percent_bp / 100).toFixed(2);
  const line = resp.suggested_lines.find((l) => l.source === 'shortfall');
  if (!line) return <span>Shortfall {pct}% of the plan</span>;
  let amount: string;
  try {
    amount = fromMinor(line.amount_minor, currency);
  } catch {
    amount = String(line.amount_minor);
  }
  return (
    <span>
      Shortfall {amount} {currency} — {pct}% of plan
    </span>
  );
}

const formatRateMicro = (micro: number) => `${Math.floor(micro / 1_000_000)}.${String(micro % 1_000_000).padStart(6, '0')}`;

/** The suggestion's own facts: rates with their dates and purpose, the shortfall percent, warnings, blockers, a failed request. */
export function DraftSuggestionInfo({
  state,
  preview,
}: {
  state: DraftState;
  preview: UseQueryResult<SettlePreviewAnswer, Error>;
}) {
  const resp = suggestionIsCurrent(state) ? state.suggestion?.response : undefined;
  // a `difference` blocker is about the bank amount in the REQUEST; the draft's own difference is shown by the form
  const blockers = resp?.blockers.filter((b) => !ADVISORY_BLOCKERS.has(b.code)) ?? [];
  const unusableFee = resp?.blockers.filter((b) => b.code === 'fee_category_unusable') ?? [];
  return (
    <div className="flex flex-col gap-1 text-xs text-muted" data-testid="settle-suggestion">
      {preview.isError && (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          <span>Couldn’t get the suggestion: {errorMessage(preview.error)}</span>
          <Button type="button" variant="secondary" size="sm" onClick={() => void preview.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {preview.isFetching && !preview.isError && <span>Updating the suggestion…</span>}
      {resp?.rates.map((r) => (
        <span key={r.currency}>
          {r.currency} rate {formatRateMicro(r.rate_micro)} of {r.rate_date} · {r.purposes.map((p) => PURPOSE_TEXT[p] ?? p).join(', ')}
        </span>
      ))}
      {/* The text prints the server's suggested shortfall, and only while the draft holds a live shortfall line: a single
          window's operator can remove it, and the percent alone would then describe a line that is not there. */}
      {liveLines(state).some((l) => l.source === 'shortfall') && shortfallText(resp, state.context.currency)}
      {resp?.warnings.map((w) => <span key={w}>{WARNING_TEXT[w] ?? w}</span>)}
      {/* In the fee mode counting from the payment amount the shortfall line is missing, and a line typed by hand is a
          fee charged on top: it lowers the bank amount and leaves the mode open — the bank amount from the statement,
          typed beside it, closes it. Anywhere else the old advice is right: the server also raises this blocker for an
          account's fee rule, where no fee mode is offered and a hand line is exactly the fee (the payment settles, the
          bank amount is the payment less the fee). */}
      {unusableFee.map((b, i) => (
        <span key={`fee-${i}`} data-testid="fee-unusable">
          No fee line suggested: its article can’t be used ({b.detail}).{' '}
          {state.sendsBank && state.anchor === 'settle'
            ? 'Type the bank amount and add the fee as a line by hand.'
            : 'Add the fee as a line by hand.'}
        </span>
      ))}
      {blockers.map((b, i) => (
        <span key={`${b.code}-${i}`} className="text-sm text-destructive">
          {b.detail}
        </span>
      ))}
    </div>
  );
}

const SOURCE_TEXT: Record<DraftLine['source'], string> = {
  account_fee: 'Account fee (suggested)',
  provider_fee: 'Provider fee (suggested)',
  shortfall: 'Kept before it arrived — from the bank amount',
  operator: '',
};

// «Fee» is the one name of the withheld kind (OH-212): what was kept from the payment, before it arrived or on top of it.
const KIND_TEXT: Record<LineKind, string> = { expense: 'Expense', income: 'Income', withheld: 'Fee' };

/**
 * The draft's lines: suggested ones (always withheld, their article and
 * amount editable) and the operator's own (kind, article, amount). A stale
 * amount and an orphan line are marked; removing a suggested line leaves the
 * draft's marker so a refetch does not bring it back. A line of a locked
 * source (`lockedSources`) is the exception to both: its amount is read-only
 * and it has no remove button; only its article can be changed.
 */
export function DraftLinesEditor({
  state,
  dispatch,
  currency,
  kinds,
  articlesFor,
  allCategories,
  disabled,
  lockedSources,
}: {
  state: DraftState;
  dispatch: Dispatch;
  currency: string;
  /** Kinds an operator line may take; the first is the default of a new line. One kind — no Kind picker. */
  kinds: readonly LineKind[];
  articlesFor: (kind: LineKind) => readonly FinanceCategory[];
  /** For the label of a suggested article the picker would not otherwise list. */
  allCategories: readonly FinanceCategory[];
  disabled?: boolean;
  /**
   * Sources whose lines are the window's own arithmetic, not the operator's to change (OH-212: the batch's automatic fee is
   * the gap to the bank amount typed). Such a line shows its Amount read-only and has no remove button; its article stays
   * editable. Without it every line can be edited and removed.
   */
  lockedSources?: readonly DraftLine['source'][];
}) {
  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Extra lines</legend>
      {state.lines.map((l, i) => {
        const options = articlesFor(l.kind).map((c) => ({ value: c.id, label: c.name }));
        if (l.categoryId && !options.some((o) => o.value === l.categoryId)) {
          const own = allCategories.find((c) => c.id === l.categoryId);
          options.unshift({ value: l.categoryId, label: own?.name ?? l.categoryId });
        }
        const showKind = l.source === 'operator' && kinds.length > 1;
        const locked = lockedSources?.includes(l.source) ?? false;
        return (
          <div
            key={l.key}
            data-testid="draft-line"
            className={
              l.orphan
                ? 'flex flex-col gap-1 rounded-[var(--radius-control)] border border-destructive p-2'
                : 'flex flex-col gap-1'
            }
          >
            {l.source !== 'operator' && <p className="text-xs text-muted">{SOURCE_TEXT[l.source]}</p>}
            {l.orphan && <p className="text-xs text-destructive">Its payment is no longer in this confirmation — remove the line.</p>}
            {/* One row at a viewport from `sm` up, where the sheet is 440px (400px of content) whatever the viewport: four equal
                columns left the Article select ~110px, and «Bank fee» showed as «Bank fe». A plain Kind label (5rem; «Fee» since
                OH-212, before it «Withheld by the channel», wrapped) gives the width it frees to the Article — 140px, with Amount at
                ~108px as before; a Kind picker keeps the equal columns. */}
            <div
              className={cn(
                'grid grid-cols-2 items-end gap-2',
                showKind ? 'sm:grid-cols-[1fr_1fr_1fr_auto]' : 'sm:grid-cols-[5rem_minmax(0,1.3fr)_minmax(0,1fr)_auto]',
              )}
            >
              {showKind ? (
                <Select
                  label="Kind"
                  value={l.kind}
                  options={kinds.map((k) => ({ value: k, label: KIND_TEXT[k] }))}
                  onChange={(e) => {
                    const kind = e.target.value as LineKind;
                    const stillOk = articlesFor(kind).some((c) => c.id === l.categoryId);
                    dispatch({ type: 'editLine', key: l.key, patch: stillOk ? { kind } : { kind, categoryId: '' } });
                  }}
                />
              ) : (
                <p className="self-center text-sm">{KIND_TEXT[l.kind]}</p>
              )}
              <Select
                label="Article"
                value={l.categoryId}
                placeholder="Pick an article"
                options={options}
                onChange={(e) => dispatch({ type: 'editLine', key: l.key, patch: { categoryId: e.target.value } })}
              />
              <MoneyInput
                label="Amount"
                currency={currency || 'EUR'}
                value={l.amount.major}
                onChange={(major) => dispatch({ type: 'editLine', key: l.key, patch: { major } })}
                error={staleFieldError(l.amount, state.context.currency)}
                disabled={disabled || locked}
              />
              {/* a locked line keeps the remove button's cell (a small ghost button with a 16px icon: 48px), so its Amount lines up with the others' */}
              {locked ? (
                <span aria-hidden="true" className="h-8 w-12" />
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => dispatch({ type: 'removeLine', key: l.key })}
                  aria-label={`Remove line ${i + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => dispatch({ type: 'addLine', kind: kinds[0] ?? 'expense', categoryId: '', major: '', targetKey: null })}
        >
          <Plus className="h-4 w-4" />
          Add line
        </Button>
      </div>
    </fieldset>
  );
}
